import { useActiveSessions } from "@/hooks/use-shopping-session";
import { useSyncFromKeep } from "@/hooks/use-sync";
import { Link } from "@tanstack/react-router";
import { RefreshCw, Settings, ShoppingCart, Tag, X } from "lucide-react";
import { useCallback, useEffect } from "react";

type AppDrawerProps = {
  open: boolean;
  onClose: () => void;
};

/** 左からスライドするナビゲーションドロワー */
export const AppDrawer = ({ open, onClose }: AppDrawerProps) => {
  const { data: sessions } = useActiveSessions();
  const sessionCount = sessions?.length ?? 0;
  const syncFromKeep = useSyncFromKeep();
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, handleKeyDown]);

  return (
    <div
      className={`fixed inset-0 z-50 ${open ? "visible" : "invisible"}`}
      aria-hidden={!open}
    >
      {/* オーバーレイ */}
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
        onKeyDown={() => {}}
        role="presentation"
      />

      {/* ドロワーパネル */}
      <nav
        className={`absolute inset-y-0 left-0 w-[240px] bg-background shadow-lg transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* ヘッダー */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-xl font-bold text-foreground">Payload Alpha</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-muted-foreground hover:bg-muted"
            aria-label="閉じる"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* ナビゲーション */}
        <ul className="flex flex-col py-2">
          <li>
            <Link
              to="/labels"
              onClick={onClose}
              className="flex items-center gap-3 px-4 py-3 text-base text-foreground hover:bg-muted"
            >
              <Tag className="h-5 w-5" />
              ラベル一覧
            </Link>
          </li>
          <li>
            <Link
              to="/shopping"
              onClick={onClose}
              className="flex items-center gap-3 px-4 py-3 text-base text-foreground hover:bg-muted"
            >
              <ShoppingCart className="h-5 w-5" />
              買い物リスト
              {sessionCount > 0 && (
                <span className="ml-auto rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                  {sessionCount}
                </span>
              )}
            </Link>
          </li>
          <li>
            <Link
              to="/settings"
              onClick={onClose}
              className="flex items-center gap-3 px-4 py-3 text-base text-foreground hover:bg-muted"
            >
              <Settings className="h-5 w-5" />
              設定
            </Link>
          </li>
        </ul>

        {/* 同期 */}
        <div className="border-t px-4 py-3">
          <button
            type="button"
            onClick={() => {
              syncFromKeep.mutate();
              onClose();
            }}
            disabled={syncFromKeep.isPending}
            className="flex w-full items-center gap-3 text-base text-foreground hover:text-primary disabled:opacity-50"
          >
            <RefreshCw
              className={`h-5 w-5 ${syncFromKeep.isPending ? "animate-spin" : ""}`}
            />
            Keep と同期
          </button>
        </div>
      </nav>
    </div>
  );
};
